import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_CubeSourceDataSourceRow, WB_DataObjectColumnRow,
    WB_MetricDefinitionRow, WB_MetricViewMetricRow,
    WB_MetricViewRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbMetricView} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_CubeSource_DataSource, esWB_DataObject_Column,
    esWB_Metric_Definition,
    esWB_Metric_View, esWB_Metric_View_Metric
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {MetricViewBOValidations} from "./MetricViewBO.Validations";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {MetricViewService} from "../PA.MD.Services/MetricViewService";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {AuditService} from "../PA.Core/Security/AuditService";
import {MetricViewBOAudit} from "./MetricViewBO.Audit";
import {ICubeSourceMetricId} from "../PA.HyperRectangle.Services/PA.HyperRectangle.Services.IF";


export interface IMetricViewLoadOptions {
    dbContext?: PAdbContext
}

const MetricViewLoadOptions_default: IMetricViewLoadOptions = {}

export class MetricViewBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_MetricView: WB_MetricViewRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Metric View'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_MetricView = undefined;
    }

    //</editor-fold>

    get WB_MetricView(): WB_MetricViewRow {
        return this.drWB_MetricView;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Metric View");

        let internalOnComplete = new Promise<IBusinessObject>( (resolve, error) => {

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Metric View.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Metric_View)
                .then((newMetricViewRow: WB_MetricViewRow) => {
                    newMetricViewRow.setDefaults()
                    newMetricViewRow.cif.MANUAL_IND='1'
                    self.drWB_MetricView = newMetricViewRow;
                    self.dbContext.add(esWB_Metric_View, newMetricViewRow)
                    self.Identity.setAll(self.WB_MetricView.cif.WB_MTRVIEW_ID, self.WB_MetricView.cif.METRIC_VIEW_ID, self.WB_MetricView.cif.METRIC_VIEW_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_MetricView, 'WB_MTRVIEW_ID', 'METRIC_VIEW_ID', 'METRIC_VIEW_NAME')


                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return  new Promise<IBusinessObject>( (resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Metric View already loaded.")
                resolve(self); // "Metric View already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, MetricViewLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Metric View.")
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = MetricViewBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = MetricViewBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aMetricViewRow: any = recordResolver.get(esWB_Metric_View, anId)

                    if (_.isEmpty(aMetricViewRow)) {
                        error("Metric View not Found");
                        return;
                    }

                    self.drWB_MetricView = aMetricViewRow;
                    self.Identity.setAll(self.WB_MetricView.cif.WB_MTRVIEW_ID, self.WB_MetricView.cif.METRIC_VIEW_ID, self.WB_MetricView.cif.METRIC_VIEW_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_MetricView, 'WB_MTRVIEW_ID', 'METRIC_VIEW_ID', 'METRIC_VIEW_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_MetricView.cif.METRIC_VIEW_NAME + "(" + self.WB_MetricView.cif.METRIC_VIEW_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Metric View.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            if (self.drWB_MetricView._rowDeleted || self.drWB_MetricView.cif.DELETED_IND =="1") {
                self._bIsValidated = true;
                resolve(self);
                return
            }
            self.ValidateMessageProvider.clearMessages()
            MetricViewBOValidations.Validate(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbMetricView = <IwbMetricView>self.drWB_MetricView;
        this.SaveProgressProvider.start("Saving " + wbMetricView.METRIC_VIEW_NAME + "(" + wbMetricView.METRIC_VIEW_ID + ")");
        let auditService: AuditService = undefined

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Metric View.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};
            let dctLeaseClasses: {[id:string]: string} = {};
            let leaseClasses: string[];
            let propsToCheck: string[] = ["METRIC_VIEW_ID", "METRIC_VIEW_NAME", "SORT_ORDER", "DELETED_IND"];

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }
            let bDone = false;

            // Add code to do individual BO save for dbContextRows....
            self.Validate({})
                .then((ignore) => {

                    if(!self._bIsValidated){
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                    if (!self.drWB_MetricView._rowExists || (self.drWB_MetricView._rowExists && self.drWB_MetricView._rowDeleted) || self.drWB_MetricView.hasChanges({properties: propsToCheck})){
                        let  viewId = self.drWB_MetricView.cif.WB_MTRVIEW_ID;
                        leaseClasses = MetricViewService.getLeaseClasses(viewId);
                        dctLeaseClasses = _.keyBy(leaseClasses, (aVal) => {
                            return aVal
                        });
                    }
                    _.forEach(self.drWB_MetricView.WB_MetricViewMetricRows, (aRel: ESRecord) => {
                        if (!aRel._rowExists || (aRel._rowExists && aRel._rowDeleted) || aRel.hasChanges({properties: [ "SORT_ORDER", "DELETED_IND", "WB_MTRDFN_ID"]})) {
                          let  viewId = (aRel as WB_MetricViewMetricRow).cif.WB_MTRVIEW_ID;

                            leaseClasses = MetricViewService.getLeaseClasses(viewId);
                            dctLeaseClasses = _.keyBy(leaseClasses, (aVal) => {
                                return aVal
                            });
                        }
                    });

                   // return saveFunction(esWB_Metric_View, self.drWB_MetricView, {})
                }).then((ignore) => {
                if (bDone) return
                return MetricViewBOAudit.MetricViewAuditBO(self)

            }).then((anAuditService) => {
                if (bDone) return
                auditService = anAuditService
                return saveFunction(esWB_Metric_View, self.drWB_MetricView, {})
            }).then((ignored) => {
                if (bDone) return
                if (auditService) {
                    return auditService.Save(bBatch)
                }
            })
                .then((ignore) => {
                    if (bDone) return
                    var tasks: Promise<any>[] = []
                    tasks.push(saveFunction(esWB_Metric_View_Metric, self.WB_MetricView.WB_MetricViewMetricRows))
                    return Promise.all(tasks)
                })
                .then((ignored) => {
                    if (bDone) return
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(()=> {
                            self._bIsSaved = true;
                            //invalidate here
                            if (!_.isEmpty(_.values(dctLeaseClasses))){
                                LeaseRegistry.generateLeaseInvalidationByClassList([ LeaseRegistry.Lease_Type_MetricView, ... _.values(dctLeaseClasses)]);
                            }
                        })
                    } else {
                        self._bIsSaved = true;
                        //invalidate leases here
                        if (!_.isEmpty(_.values(dctLeaseClasses))){
                            LeaseRegistry.generateLeaseInvalidationByClassList([LeaseRegistry.Lease_Type_MetricView, ... _.values(dctLeaseClasses)]);
                        }

                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err);
                    resolve(self)
                })


        });
    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbMetricView = <IwbMetricView>self.drWB_MetricView;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbMetricView.METRIC_VIEW_NAME + "(" + wbMetricView.METRIC_VIEW_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Metric View

            // 2. If Valid, mark all the records for deletion.
            self.drWB_MetricView.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: MetricViewBO = this;

        const tgt = new MetricViewBO({});
        await tgt.LoadNew()

        if(!tgt.bIsLoaded)
            throw new Error("Unable to create New Metric View")

        RecordUtils.CopyProperties(tgt.dbContext, esWB_Metric_View, src.WB_MetricView, tgt.WB_MetricView,
            [],
            ['METRIC_VIEW_ID', 'METRIC_VIEW_NAME', 'INTEGRATION_IND','CREATED_DTTS','MODIFIED_DTTS', 'DELETED_DT', 'DELETED_IND'],
            {})
        tgt.WB_MetricView.cif.METRIC_VIEW_ID = "CopyOf" + src.WB_MetricView.cif.METRIC_VIEW_ID
        tgt.WB_MetricView.cif.METRIC_VIEW_NAME = "Copy of " + src.WB_MetricView.cif.METRIC_VIEW_NAME
        tgt.Identity.setAll(tgt.WB_MetricView.cif.WB_MTRVIEW_ID, tgt.WB_MetricView.cif.METRIC_VIEW_ID, tgt.WB_MetricView.cif.METRIC_VIEW_NAME);

        for(let aRow of src.WB_MetricView.WB_MetricViewMetricRows){
            if(!aRow.isActive)
                continue

            if (tgt.WB_MetricView.cif.WB_CUBESOURCE_ID > 0) {
                await tgt.addCubeSourceMetricRow({
                        MetricId: aRow.MetricId,
                        MetricName: aRow.MetricName,
                        WB_MetricDefinitionRow: aRow.WB_MetricDefinitionRow,
                        WB_DataObjectColumnRow: aRow.WB_DataObjectColumnRow,
                        WB_CubeSourceDataSourceRow: aRow.WB_CubeSourceDataSourceRow,
                        Comments: "",
                        GroupName: "",
                        TechnicalName: aRow.MetricId,
                        UOMConverterCSVDenom: "",
                        UOMConverterCSVNum: "",
                        UOMDenom: "",
                        UOMNum: "",
                        Version: ""
                    },
                    {sort: aRow.cif.SORT_ORDER})
            } else {
                await tgt.addMetricRow(aRow.cif.WB_MTRDFN_ID, {sort: aRow.cif.SORT_ORDER})
            }

        }

        return tgt
    }



    //</editor-fold>


    //<editor-fold desc = "Metric View Operations">

    addMetricRow(WB_MTRDFN_ID: number, options: any | {sort: number, manual: boolean}): Promise<WB_MetricViewMetricRow> {
        const self = this

        return new Promise<WB_MetricViewMetricRow>((resolve, error) => {

            let aMetricIdRow: WB_MetricDefinitionRow = undefined
            self.dbContext.find(esWB_Metric_Definition, WB_MTRDFN_ID)
                .then((aMetricDefinitionRow: WB_MetricDefinitionRow) => {
                    if (_.isEmpty(aMetricDefinitionRow)) {
                        // Bad Metric Definition ID
                        resolve(undefined)
                        return
                    }
                    aMetricIdRow = aMetricDefinitionRow
                    // create new records
                    return self.dbContext.createRecordWithKey(esWB_Metric_View_Metric)

                })
                .then((aNewRow: WB_MetricViewMetricRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_MTRVIEW_ID = self.drWB_MetricView.cif.WB_MTRVIEW_ID
                    aNewRow.cif.MANUAL_IND="1"
                    if(_.isNumber(options?.sort))
                        aNewRow.cif.SORT_ORDER = options.sort
                    if(_.isBoolean(options?.manual))
                        aNewRow.cif.MANUAL_IND = (options.manual)?"1": "0"

                    aNewRow.WB_MetricDefinitionRow = aMetricIdRow
                    self.dbContext.add(esWB_Metric_View_Metric, aNewRow)
                    resolve(aNewRow)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeMetricRow(aWB_MTRVIEWR_ID: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_MetricView.WB_MetricViewMetricRows, (aMetricIdRow: WB_MetricViewMetricRow) => {
                if(aMetricIdRow.isActive && (aMetricIdRow.cif.WB_MTRVIEWR_ID == aWB_MTRVIEWR_ID)) {
                    aMetricIdRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    async addCubeSourceMetricRow(metricId: ICubeSourceMetricId, options?: any | {sort: number, manual: boolean}): Promise<WB_MetricViewMetricRow> {

        if(_.isEmpty(metricId))
            return

        for(let aRow of this.WB_MetricView.WB_MetricViewMetricRows){
            if(!aRow.isActive)
                continue
            if(aRow.MetricId == metricId.MetricId)
                return aRow
        }

        let opts = options|| {}

        if(metricId.WB_MetricDefinitionRow)
            return await this.addMetricRow(metricId.WB_MetricDefinitionRow.cif.WB_MTRDFN_ID, opts)

        if(metricId.WB_CubeSourceDataSourceRow && metricId.WB_DataObjectColumnRow){
            let aDSRRow: WB_CubeSourceDataSourceRow = <any> await this.dbContext.find(esWB_CubeSource_DataSource, metricId.WB_CubeSourceDataSourceRow.cif.WB_CUBESOURCEDSR_ID)
            let aDOCRow: WB_DataObjectColumnRow = <any> await this.dbContext.find(esWB_DataObject_Column, metricId.WB_DataObjectColumnRow.cif.WB_DATAOBJECT_COLUMN_ID)
            if(_.isEmpty(aDSRRow) || _.isEmpty(aDOCRow))
                return undefined

            let aNewRow: WB_MetricViewMetricRow = <any> await this.dbContext.createRecordWithKey(esWB_Metric_View_Metric)
            aNewRow.setDefaults()
            aNewRow.cif.WB_MTRVIEW_ID = this.drWB_MetricView.cif.WB_MTRVIEW_ID
            aNewRow.WB_MetricViewRow = this.drWB_MetricView
            aNewRow.cif.WB_CUBESOURCEDSR_ID = aDSRRow.cif.WB_CUBESOURCEDSR_ID
            aNewRow.WB_CubeSourceDataSourceRow = aDSRRow
            aNewRow.cif.WB_DATAOBJECT_COLUMN_ID = aDOCRow.cif.WB_DATAOBJECT_COLUMN_ID
            aNewRow.WB_DataObjectColumnRow = aDOCRow
            aNewRow.cif.SORT_ORDER = opts.sort || 0
            if(opts?.manual)
                aNewRow.cif.MANUAL_IND = '1'
            this.dbContext.add(esWB_Metric_View_Metric, aNewRow)
            return aNewRow
        }

        return undefined
    }


//</editor-fold>


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
            },
            dctIncludeBelongsToNamedRelations: {
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Metric_View,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }
};
PAEnvironment.registerBusinessObjectClass( MetricViewBO , "MetricViewBO", "Metric View")